/**
 * Question Editor Component
 * Form for creating/editing questions and answer options
 */

import React, { useState, useEffect } from 'react';
import { CreateQuestionInput, CreateAnswerOptionInput, QuestionType, QuestionWithOptions } from '../../types/models';
import { questionsApi } from '../api';
import { MediaImage } from './MediaImage';

interface QuestionEditorProps {
  onSave: (question: CreateQuestionInput, options: CreateAnswerOptionInput[]) => void;
  onCancel: () => void;
  question?: QuestionWithOptions; // Optional: if provided, we're editing
}

type EditorOption = { text: string; isCorrect: boolean; points?: number; id?: string; mediaUrl?: string | null; mediaWidth?: number | null; mediaHeight?: number | null };

/**
 * Build a single answer option payload from editor state.
 * For rating scales, calculate partial_credit_weight based on points
 * (partial_credit_weight = option_points / question_points_possible, clamped to [0, 1]).
 */
function buildAnswerOption(
  opt: EditorOption,
  index: number,
  isRatingScale: boolean,
  pointsPossible: number
): CreateAnswerOptionInput {
  let partialCreditWeight: number = 0;
  if (isRatingScale) {
    if (opt.points === undefined || opt.points === null) {
      throw new Error(`Option ${index + 1} must have points set for rating scale questions`);
    }
    const points = Number.parseFloat(opt.points.toString());
    if (Number.isNaN(points) || points < 0) {
      throw new Error(`Option ${index + 1} has invalid points value: ${opt.points}`);
    }
    if (pointsPossible > 0) {
      partialCreditWeight = points / pointsPossible;
      // Ensure it's between 0 and 1
      partialCreditWeight = Math.max(0, Math.min(1, partialCreditWeight));
    } else {
      throw new Error('Points possible must be greater than 0 for rating scale questions');
    }
  } else {
    // For non-rating scale questions, use 1.0 for correct, 0 for incorrect
    partialCreditWeight = opt.isCorrect ? 1.0 : 0;
  }

  const optionData: any = {
    question_id: '', // Will be set by parent
    sequence_order: index + 1,
    option_text: opt.text.trim(),
    is_correct: isRatingScale ? false : opt.isCorrect, // Rating scales don't use is_correct
    partial_credit_weight: partialCreditWeight,
  };

  if (opt.mediaUrl) {
    optionData.option_media_url = opt.mediaUrl;
    // Forward stored dimensions so reserved space survives save/reload.
    optionData.option_media_width = opt.mediaWidth ?? null;
    optionData.option_media_height = opt.mediaHeight ?? null;
  }

  // Include ID if editing (for tracking which options to update vs create)
  if (opt.id) {
    optionData.id = opt.id;
  }

  return optionData;
}

export const QuestionEditor: React.FC<QuestionEditorProps> = ({ onSave, onCancel, question }) => {
  const isEditing = !!question;

  const [questionType, setQuestionType] = useState<QuestionType>('multiple_choice');
  const [promptText, setPromptText] = useState('');
  const [pointsPossible, setPointsPossible] = useState(1.0);
  const [explanation, setExplanation] = useState('');
  const [category, setCategory] = useState('');
  const [allowPartialCredit, setAllowPartialCredit] = useState(false);
  const [isRatingScale, setIsRatingScale] = useState(false);
  const [promptMediaUrl, setPromptMediaUrl] = useState<string | null>(null);
  // Intrinsic dimensions of the prompt image, so the preview reserves space and
  // avoids the first-load layout shift (matches how the player renders it).
  const [promptMediaWidth, setPromptMediaWidth] = useState<number | null>(null);
  const [promptMediaHeight, setPromptMediaHeight] = useState<number | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [options, setOptions] = useState<Array<EditorOption>>([
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ]);
  const [uploadingOptionId, setUploadingOptionId] = useState<string | null>(null);
  const [optionImageError, setOptionImageError] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    setImageError(null);
    setOptionImageError({});
    setUploadingOptionId(null);
    if (question) {
      setQuestionType(question.question_type);
      setPromptText(question.prompt_text);
      setPointsPossible(question.points_possible || 1.0);
      setExplanation(question.explanation || '');
      setCategory(question.category || '');
      setAllowPartialCredit(question.allow_partial_credit || false);
      setPromptMediaUrl(question.prompt_media_url || null);
      setPromptMediaWidth(question.prompt_media_width ?? null);
      setPromptMediaHeight(question.prompt_media_height ?? null);
      
      // Check if this is a rating scale (any option has partial_credit_weight > 0 and < 1)
      const hasPartialWeights = question.answer_options.some(
        opt => opt.partial_credit_weight > 0 && opt.partial_credit_weight < 1
      );
      setIsRatingScale(hasPartialWeights);
      
      // Populate options
      if (question.answer_options && question.answer_options.length > 0) {
        const populatedOptions = question.answer_options.map(opt => {
          const base = {
            id: opt.id,
            text: opt.option_text,
            mediaUrl: opt.option_media_url || null,
            mediaWidth: opt.option_media_width ?? null,
            mediaHeight: opt.option_media_height ?? null,
          };
          if (hasPartialWeights) {
            const points = (opt.partial_credit_weight || 0) * (question.points_possible || 1.0);
            return { ...base, isCorrect: false, points };
          }
          return { ...base, isCorrect: opt.is_correct || false, points: undefined };
        });
        setOptions(populatedOptions);
      } else {
        setOptions([{ text: '', isCorrect: false }, { text: '', isCorrect: false }]);
      }
    } else {
      // Reset form for new question
      setQuestionType('multiple_choice');
      setPromptText('');
      setPointsPossible(1.0);
      setExplanation('');
      setCategory('');
      setIsRatingScale(false);
      setPromptMediaUrl(null);
      setPromptMediaWidth(null);
      setPromptMediaHeight(null);
      setOptions([{ text: '', isCorrect: false }, { text: '', isCorrect: false }]);
    }
  }, [question]);

  const handleAddOption = () => {
    setOptions([...options, { text: '', isCorrect: false }]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index: number, field: 'text' | 'isCorrect' | 'points', value: string | boolean | number) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    if (!question?.id) {
      setImageError('Please save the question first before uploading an image.');
      input.value = '';
      return;
    }

    const isValidImage = file.type.startsWith('image/') ||
                        file.type === 'image/svg+xml' ||
                        file.name.toLowerCase().endsWith('.svg');
    if (!isValidImage) {
      setImageError('Please select an image file (JPG, PNG, GIF, SVG, etc.)');
      input.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setImageError('Image file size must be less than 10MB');
      input.value = '';
      return;
    }

    try {
      setUploadingImage(true);
      setImageError(null);
      const result = await questionsApi.uploadImage(question.id, file);
      setPromptMediaUrl(result.prompt_media_url);
      setPromptMediaWidth(result.prompt_media_width ?? null);
      setPromptMediaHeight(result.prompt_media_height ?? null);
    } catch (err: any) {
      setImageError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      input.value = '';
    }
  };

  const handleImageDelete = async () => {
    if (!question?.id || !promptMediaUrl) return;
    if (!window.confirm('Remove this image from the question?')) return;

    try {
      setUploadingImage(true);
      setImageError(null);
      await questionsApi.deleteImage(question.id);
      setPromptMediaUrl(null);
      setPromptMediaWidth(null);
      setPromptMediaHeight(null);
    } catch (err: any) {
      setImageError(err.message || 'Failed to delete image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOptionImageUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;

    const option = options[index];
    const optionId = option?.id;

    if (!question?.id || !optionId) {
      setOptionImageError({
        ...optionImageError,
        [String(index)]: 'Save the question first so this option exists, then upload the image.',
      });
      input.value = '';
      return;
    }

    const isValidImage = file.type.startsWith('image/') ||
                        file.type === 'image/svg+xml' ||
                        file.name.toLowerCase().endsWith('.svg');
    if (!isValidImage) {
      setOptionImageError({ ...optionImageError, [optionId]: 'Please select an image file.' });
      input.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setOptionImageError({ ...optionImageError, [optionId]: 'Image must be less than 10MB.' });
      input.value = '';
      return;
    }

    try {
      setUploadingOptionId(optionId);
      setOptionImageError({ ...optionImageError, [optionId]: '' });
      const result = await questionsApi.uploadOptionImage(question.id, optionId, file);
      const newOptions = [...options];
      newOptions[index] = {
        ...newOptions[index],
        mediaUrl: result.option_media_url,
        mediaWidth: result.option_media_width ?? null,
        mediaHeight: result.option_media_height ?? null,
      };
      setOptions(newOptions);
    } catch (err: any) {
      setOptionImageError({
        ...optionImageError,
        [optionId]: err.message || 'Failed to upload image',
      });
    } finally {
      setUploadingOptionId(null);
      input.value = '';
    }
  };

  const handleOptionImageDelete = async (index: number) => {
    const option = options[index];
    const optionId = option?.id;
    if (!question?.id || !optionId || !option.mediaUrl) return;
    if (!window.confirm('Remove this image from the option?')) return;

    try {
      setUploadingOptionId(optionId);
      setOptionImageError({ ...optionImageError, [optionId]: '' });
      await questionsApi.deleteOptionImage(question.id, optionId);
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], mediaUrl: null, mediaWidth: null, mediaHeight: null };
      setOptions(newOptions);
    } catch (err: any) {
      setOptionImageError({
        ...optionImageError,
        [optionId]: err.message || 'Failed to delete image',
      });
    } finally {
      setUploadingOptionId(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!promptText.trim()) {
      alert('Please enter a question prompt');
      return;
    }

    const validOptions = options.filter((opt) => opt.text.trim());
    if (validOptions.length === 0) {
      alert('Please add at least one answer option');
      return;
    }

    // For rating scales, validate points are set
    if (isRatingScale) {
      const hasInvalidPoints = validOptions.some((opt) => opt.points === undefined || opt.points < 0);
      if (hasInvalidPoints) {
        alert('Please set points for each option in rating scale mode');
        return;
      }
    } else {
      // For regular questions, validate that at least one option is correct
      const hasCorrect = validOptions.some((opt) => opt.isCorrect);
      if (!hasCorrect) {
        alert('Please mark at least one answer as correct');
        return;
      }

      // For multiple choice and true/false, ensure only one is correct
      if ((questionType === 'multiple_choice' || questionType === 'true_false') && validOptions.filter((opt) => opt.isCorrect).length > 1) {
        alert(`${questionType === 'multiple_choice' ? 'Multiple choice' : 'True/false'} questions can only have one correct answer`);
        return;
      }
    }

    const questionData: CreateQuestionInput = {
      assessment_id: question?.assessment_id || undefined, // Will be set by parent if needed
      sequence_order: question?.sequence_order || 1, // Will be calculated by parent if new
      question_type: questionType,
      prompt_text: promptText.trim(),
      points_possible: pointsPossible,
      explanation: explanation.trim() || null,
      category: category.trim() || null,
      allow_partial_credit: questionType === 'multi_select' ? allowPartialCredit : undefined,
    };

    const answerOptions: CreateAnswerOptionInput[] = validOptions.map((opt, index) =>
      buildAnswerOption(opt, index, isRatingScale, pointsPossible)
    );

    onSave(questionData, answerOptions);
    
    // Reset form only if not editing (editing mode will be handled by parent)
    if (!isEditing) {
      setPromptText('');
      setPointsPossible(1.0);
      setExplanation('');
      setCategory('');
      setIsRatingScale(false);
      setOptions([{ text: '', isCorrect: false }, { text: '', isCorrect: false }]);
    }
  };

  let promptImageContent: React.ReactNode;
  if (!isEditing) {
    promptImageContent = (
      <div style={{
        padding: '0.75rem',
        backgroundColor: '#e8f4f8',
        border: '1px solid #3498db',
        borderRadius: '4px',
        color: '#2c3e50',
        fontSize: '0.9rem',
      }}>
        Save the question first, then reopen it to upload an image.
      </div>
    );
  } else if (promptMediaUrl) {
    promptImageContent = (
      <div className="image-preview-container">
        <div className="image-preview">
          <MediaImage
            src={promptMediaUrl}
            alt="Question prompt preview"
            width={promptMediaWidth}
            height={promptMediaHeight}
          />
        </div>
        <div className="image-actions">
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            {uploadingImage ? 'Uploading...' : 'Replace Image'}
            <input
              type="file"
              accept="image/*,.svg"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              style={{ display: 'none' }}
            />
          </label>
          <button
            type="button"
            onClick={handleImageDelete}
            disabled={uploadingImage}
            className="btn btn-secondary"
          >
            Delete Image
          </button>
        </div>
      </div>
    );
  } else {
    promptImageContent = (
      <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
        {uploadingImage ? 'Uploading...' : 'Upload Image'}
        <input
          type="file"
          accept="image/*,.svg"
          onChange={handleImageUpload}
          disabled={uploadingImage}
          style={{ display: 'none' }}
        />
      </label>
    );
  }

  return (
    <div className="question-editor">
      <h3>{isEditing ? 'Edit Question' : 'Add New Question'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="question-type">
            Question Type <span className="required">*</span>
          </label>
          <select
            id="question-type"
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value as QuestionType)}
            required
          >
            <option value="multiple_choice">Multiple Choice</option>
            <option value="multi_select">Multi-Select</option>
            <option value="true_false">True/False</option>
            <option value="short_answer">Short Answer</option>
            <option value="matching">Matching</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="question-prompt">
            Question Prompt <span className="required">*</span>
          </label>
          <textarea
            id="question-prompt"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            placeholder="Enter your question here"
            rows={3}
            required
          />
        </div>

        <div className="form-group">
          <label>Prompt Image (optional)</label>
          <small style={{ display: 'block', marginTop: '0.25rem', marginBottom: '0.5rem', color: '#7f8c8d' }}>
            Attach an image that the prompt can reference. Shown above the question during the assessment.
          </small>
          {promptImageContent}
          {imageError && (
            <div className="error" style={{ marginTop: '0.5rem' }}>{imageError}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="question-category">Category (optional)</label>
          <input
            id="question-category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g., Integrity, Work Ethic, Professionalism"
          />
          <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
            Group questions by category (e.g., "Integrity", "Work Ethic")
          </small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="points-possible">
              Points Possible
            </label>
            <input
              id="points-possible"
              type="number"
              value={pointsPossible}
              onChange={(e) => setPointsPossible(Number.parseFloat(e.target.value) || 1.0)}
              min="0"
              step="0.1"
              required
            />
          </div>
          {questionType === 'multiple_choice' && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={isRatingScale}
                  onChange={(e) => setIsRatingScale(e.target.checked)}
                />{' '}
                Rating Scale (award points based on selection)
              </label>
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
                Each option awards different points (e.g., 1, 2, 3, 4)
              </small>
            </div>
          )}
          {questionType === 'multi_select' && (
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={allowPartialCredit}
                  onChange={(e) => setAllowPartialCredit(e.target.checked)}
                />{' '}
                Allow Partial Credit
              </label>
              <small style={{ display: 'block', marginTop: '0.25rem', color: '#7f8c8d' }}>
                Award points based on ratio of correct selections (e.g., 3/5 correct = 60% credit)
              </small>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="question-explanation">Explanation (shown after submission)</label>
          <textarea
            id="question-explanation"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Optional explanation for the correct answer"
            rows={2}
          />
        </div>

        <div className="answer-options-section">
          <div className="section-header">
            <label>Answer Options <span className="required">*</span></label>
            <button type="button" onClick={handleAddOption} className="btn btn-small">
              Add Option
            </button>
          </div>

          {options.map((option, index) => {
            const optionKey = option.id || String(index);
            const optionError = optionImageError[option.id || ''] || optionImageError[String(index)];
            const showImageControls = questionType !== 'true_false';
            const isUploadingThisOption = uploadingOptionId === option.id;
            return (
              <div key={optionKey} className="option-row-group">
                <div className="option-row">
                  <input
                    type="text"
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                    placeholder={isRatingScale ? `Option ${index + 1} (e.g., Rarely, Sometimes, Usually, Always)` : `Option ${index + 1}`}
                    className="option-input"
                  />
                  {isRatingScale ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label htmlFor={`option-points-${optionKey}`} style={{ whiteSpace: 'nowrap' }}>Points:</label>
                      <input
                        id={`option-points-${optionKey}`}
                        type="number"
                        value={option.points || ''}
                        onChange={(e) => handleOptionChange(index, 'points', Number.parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.1"
                        style={{ width: '60px', padding: '0.25rem' }}
                        required
                      />
                    </div>
                  ) : (
                    <label className="correct-checkbox">
                      <input
                        type={questionType === 'multi_select' ? 'checkbox' : 'radio'}
                        name="correct-option"
                        checked={option.isCorrect}
                        onChange={(e) => {
                          if (questionType === 'multiple_choice' || questionType === 'true_false') {
                            // For radio buttons, uncheck all others
                            setOptions(options.map((opt, i) => ({
                              ...opt,
                              isCorrect: i === index ? e.target.checked : false,
                            })));
                          } else {
                            handleOptionChange(index, 'isCorrect', e.target.checked);
                          }
                        }}
                      />{' '}
                      Correct
                    </label>
                  )}
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(index)}
                      className="btn btn-small btn-danger"
                    >
                      Remove
                    </button>
                  )}
                </div>
                {showImageControls && (
                  <div className="option-image-row">
                    {option.mediaUrl ? (
                      <>
                        <MediaImage className="option-image-thumb" src={option.mediaUrl} alt={`Option ${index + 1}`} width={option.mediaWidth} height={option.mediaHeight} />
                        <label className="btn btn-small btn-secondary" style={{ cursor: 'pointer' }}>
                          {isUploadingThisOption ? 'Uploading...' : 'Replace'}
                          <input
                            type="file"
                            accept="image/*,.svg"
                            onChange={(e) => handleOptionImageUpload(index, e)}
                            disabled={isUploadingThisOption}
                            style={{ display: 'none' }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => handleOptionImageDelete(index)}
                          disabled={isUploadingThisOption}
                          className="btn btn-small btn-secondary"
                        >
                          Remove Image
                        </button>
                      </>
                    ) : (
                      <label className="btn btn-small btn-secondary" style={{ cursor: 'pointer' }}>
                        {isUploadingThisOption ? 'Uploading...' : 'Add Image'}
                        <input
                          type="file"
                          accept="image/*,.svg"
                          onChange={(e) => handleOptionImageUpload(index, e)}
                          disabled={isUploadingThisOption}
                          style={{ display: 'none' }}
                        />
                      </label>
                    )}
                    {!option.id && (
                      <small style={{ color: '#7f8c8d' }}>
                        Save the question first to attach an image to this option.
                      </small>
                    )}
                  </div>
                )}
                {optionError && (
                  <div className="error" style={{ marginTop: '0.25rem' }}>{optionError}</div>
                )}
              </div>
            );
          })}
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">
            {isEditing ? 'Update Question' : 'Add Question'}
          </button>
          <button type="button" onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
